/** Squat analysis report tab */
import { IonCardContent, IonCol, IonGrid, IonIcon, IonImg, IonRow, IonText, IonThumbnail } from '@ionic/react';
import { helpCircleOutline } from 'ionicons/icons';

import { BulletList } from '@/components/BulletList/BulletList';
import { CardHeader } from '@/components/CardHeader/cardHeader';
import { Card } from '@/components/Cards';
import { DataTable } from '@/components/DataTable/DataTable';
import { DataTableColumn } from '@/components/DataTable/DataTableColumn';
import { DataTableHeader } from '@/components/DataTable/DataTableHeader';
import { DataTableRow } from '@/components/DataTable/DataTableRow';
import { DataTableSection } from '@/components/DataTable/DataTableSection';
import { Tooltip } from '@/components/Tooltip/Tooltip';

import SingleLegSquatAvatar from '@/assets/avatars/squat/singleLegSquatAvatar.png';
import HipCoronalMovement from '@/assets/icons/kinematicsIcons/hipCoronalMovement.png';
import HipTransversalMovement from '@/assets/icons/kinematicsIcons/hipTransversalMovement.png';
import KneeCoronalMovement from '@/assets/icons/kinematicsIcons/kneeCoronalMovement.png';

import { SLSquatAnalysisData } from '@/models/reportV2.model';

import styles from '@/pages/ReportV2/Styles/Reports.module.css';

type SLSquatReportProps = {
  tabData: SLSquatAnalysisData;
};

/** The Walk analysis report tab
 * @param report - The full walk report info and data
 */
export const SingleLegSquatAnalysisReportTab = ({ tabData }: SLSquatReportProps) => {
  const colors = ['tableLabel_succes', 'tableLabel_warning', 'tableLabel_danger', 'tableLabel_succes'];
  function getColor(index: number) {
    return colors[index % colors.length];
  }

  if (!tabData) {
    return <IonText>Data not found.</IonText>;
  }
  return (
    <>
      <IonRow className='ion-padding-vertical'>
        <Card style={{ height: 'fit-content', margin: 0 }}>
          <CardHeader
            title={
              <>
                <IonGrid className='ion-no-padding' fixed>
                  <IonRow className='ion-no-padding ion-justify-content-center'>
                    <IonCol size='5' style={{ marginRight: '4px' }}>
                      <IonRow className='ion-justify-content-center ion-padding-bottom'>
                        <IonText className={styles.title}>Left Leg</IonText>
                      </IonRow>
                      <IonRow className='ion-justify-content-center'>
                        <IonCol className='ion-text-center' size='6'>
                          <b>Rep Time:</b> {tabData.repTime_Left} (± {tabData.stdRepTime_Left}) sec.
                        </IonCol>
                        <IonCol className='ion-text-center' size='6'>
                          <b>Q-Score:</b>{' '}
                          <span
                            className={
                              tabData.qScore_Left === 1
                                ? styles.qualityLabel_succes
                                : tabData.qScore_Left === 4
                                  ? styles.qualityLabel_danger
                                  : styles.qualityLabel_warning
                            }
                            id='showQualityLabel'>
                            {tabData.qScoreLabel_Left}
                          </span>
                        </IonCol>
                      </IonRow>
                    </IonCol>
                    <span style={{ width: '1px', backgroundColor: '#e0e0e0' }} />
                    <IonCol size='5' style={{ marginLeft: '4px' }}>
                      <IonRow className='ion-justify-content-center  ion-padding-bottom'>
                        <IonText className={styles.title}>Right Leg</IonText>
                      </IonRow>
                      <IonRow className='ion-justify-content-center'>
                        <IonCol className='ion-text-center' size='6'>
                          <b>Rep Time:</b> {tabData.repTime_Right} (± {tabData.stdRepTime_Right}) sec.
                        </IonCol>
                        <IonCol className='ion-text-center' size='6'>
                          <b>Q-Score:</b>{' '}
                          <span
                            className={
                              tabData.qScore_Right === 1
                                ? styles.qualityLabel_succes
                                : tabData.qScore_Right === 4
                                  ? styles.qualityLabel_danger
                                  : styles.qualityLabel_warning
                            }
                            id='showQualityLabel'>
                            {tabData.qScoreLabel_Right}
                          </span>
                        </IonCol>
                      </IonRow>
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </>
            }
          />
        </Card>
      </IonRow>
      <IonRow className='ion-padding-bottom'>
        <Card style={{ margin: 0 }}>
          <IonCardContent>
            <IonRow className='ion-align-items-center ion-justify-content-center'>
              <IonCol className='' size='5'>
                <IonImg className={styles.squatsAvatar} src={SingleLegSquatAvatar} />
              </IonCol>
              <IonCol className='ion-no-padding' size='7'>
                <DataTable>
                  <DataTableHeader titles={['', 'Left', 'Right', 'Symmetry']} />

                  <DataTableSection>
                    <DataTableRow>
                      <DataTableColumn type='key'>Pelvis</DataTableColumn>
                      <DataTableColumn type='value'>
                        {tabData.romData[0][0].toFixed(1) + '°'} ± {tabData.romData[0][1].toFixed(1) + '°'}
                      </DataTableColumn>
                      <DataTableColumn type='value'>
                        {tabData.romData[0][2].toFixed(1) + '°'} ± {tabData.romData[0][3].toFixed(1) + '°'}
                      </DataTableColumn>
                      <DataTableColumn type='value'>{tabData.romData[0][4].toFixed(0) + '%'}</DataTableColumn>
                    </DataTableRow>
                  </DataTableSection>
                  <DataTableSection>
                    <DataTableRow>
                      <DataTableColumn type='key'>Hip</DataTableColumn>
                      <DataTableColumn type='value'>
                        {tabData.romData[1][0].toFixed(1) + '°'} ± {tabData.romData[1][1].toFixed(1) + '°'}
                      </DataTableColumn>
                      <DataTableColumn type='value'>
                        {tabData.romData[1][2].toFixed(1) + '°'} ± {tabData.romData[1][3].toFixed(1) + '°'}
                      </DataTableColumn>
                      <DataTableColumn type='value'>{tabData.romData[1][4].toFixed(0) + '%'}</DataTableColumn>
                    </DataTableRow>
                    <DataTableRow>
                      <DataTableColumn indent type='key'>
                        External rotation <IonIcon id='trigger-hip-external-rotation' src={helpCircleOutline} />
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[2][0])} type='value'>
                        {tabData.hipLabels.externalRotationLabel_Left}
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[2][1])} type='value'>
                        {tabData.hipLabels.externalRotationLabel_Right}
                      </DataTableColumn>
                      <DataTableColumn />
                    </DataTableRow>
                    <DataTableRow>
                      <DataTableColumn indent type='key'>
                        Internal rotation <IonIcon id='trigger-hip-internal-rotation' src={helpCircleOutline} />
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[3][0])} type='value'>
                        {tabData.hipLabels.internalRotationLabel_Left}
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[3][1])} type='value'>
                        {tabData.hipLabels.internalRotaionLabel_Right}
                      </DataTableColumn>
                      <DataTableColumn />
                    </DataTableRow>
                    <DataTableRow>
                      <DataTableColumn indent type='key'>
                        Abduction <IonIcon id='trigger-hip-abduction' src={helpCircleOutline} />
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[4][0])} type='value'>
                        {tabData.hipLabels.abductionLabel_Left}
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[4][1])} type='value'>
                        {tabData.hipLabels.abductionLabel_Right}
                      </DataTableColumn>
                      <DataTableColumn />
                    </DataTableRow>
                    <DataTableRow>
                      <DataTableColumn indent type='key'>
                        Adduction <IonIcon id='trigger-hip-adduction' src={helpCircleOutline} />
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[5][0])} type='value'>
                        {tabData.hipLabels.adductionLabel_Left}
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[5][1])} type='value'>
                        {tabData.hipLabels.adductionLabel_Right}
                      </DataTableColumn>
                      <DataTableColumn />
                    </DataTableRow>
                  </DataTableSection>

                  <DataTableSection>
                    <DataTableRow>
                      <DataTableColumn type='key'>Knee</DataTableColumn>
                      <DataTableColumn type='value'>
                        {tabData.romData[2][0].toFixed(1) + '°'} ± {tabData.romData[2][1].toFixed(1) + '°'}
                      </DataTableColumn>
                      <DataTableColumn type='value'>
                        {tabData.romData[2][2].toFixed(1) + '°'} ± {tabData.romData[2][3].toFixed(1) + '°'}
                      </DataTableColumn>
                      <DataTableColumn type='value'>{tabData.romData[2][4].toFixed(0) + '%'}</DataTableColumn>
                    </DataTableRow>
                    <DataTableRow>
                      <DataTableColumn indent type='key'>
                        Varus / Valgus <IonIcon id='trigger-knee-valgus' src={helpCircleOutline} />
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[6][0])} type='value'>
                        {tabData.varusValgusLabel_Left}
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[6][1])} type='value'>
                        {tabData.varusValgusLabel_Right}
                      </DataTableColumn>
                      <DataTableColumn />
                    </DataTableRow>
                  </DataTableSection>

                  <DataTableSection>
                    <DataTableRow>
                      <DataTableColumn type='key'>Ankle</DataTableColumn>
                      <DataTableColumn type='value'>
                        {tabData.romData[3][0].toFixed(1) + '°'} ± {tabData.romData[3][1].toFixed(1) + '°'}
                      </DataTableColumn>
                      <DataTableColumn type='value'>
                        {tabData.romData[3][2].toFixed(1) + '°'} ± {tabData.romData[3][3].toFixed(1) + '°'}
                      </DataTableColumn>
                      <DataTableColumn type='value'>{tabData.romData[3][4].toFixed(0) + '%'}</DataTableColumn>
                    </DataTableRow>
                    <DataTableRow>
                      <DataTableColumn indent type='key'>
                        Early heel raise
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[0][0])} type='value'>
                        {tabData.ankleLabels.earlyHeelRaiseLabel_Left}
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[0][1])} type='value'>
                        {tabData.ankleLabels.earlyHeelRaiseLabel_Right}
                      </DataTableColumn>
                      <DataTableColumn />
                    </DataTableRow>
                  </DataTableSection>
                </DataTable>
              </IonCol>
            </IonRow>
          </IonCardContent>
        </Card>
      </IonRow>

      <Tooltip
        alignment='center'
        dismissOnSelect={true}
        side='bottom'
        trigger='trigger-hip-external-rotation'
        triggerAction='click'>
        <IonGrid>
          <IonRow className='ion-align-items-center'>
            <IonCol className='ion-padding-end' size='auto'>
              <IonThumbnail>
                <IonImg src={HipTransversalMovement} />
              </IonThumbnail>
            </IonCol>
            <IonCol>
              <BulletList
                listItems={[
                  'Rotation <0°: Hip endorotates',
                  'Fair: Rotation between 10° & 20°',
                  'Poor: Rotation more than 20°',
                  'Rotation <10°: Rotation is less than 10°',
                ]}
              />
            </IonCol>
          </IonRow>
        </IonGrid>
      </Tooltip>
      <Tooltip
        alignment='center'
        dismissOnSelect={true}
        side='bottom'
        trigger='trigger-hip-abduction'
        triggerAction='click'>
        <IonGrid>
          <IonRow className='ion-align-items-center'>
            <IonCol className='ion-padding-end' size='auto'>
              <IonThumbnail>
                <IonImg src={HipCoronalMovement} />
              </IonThumbnail>
            </IonCol>
            <IonCol>
              <BulletList
                listItems={[
                  'Abduction <10°: Abduction less than 10°',
                  'Fair: Abduction between 10° & 20°',
                  'Poor: Abduction more than 20°',
                  'Abduction <0°: Adduction detected°',
                ]}
              />
            </IonCol>
          </IonRow>
        </IonGrid>
      </Tooltip>
      <Tooltip
        alignment='center'
        dismissOnSelect={true}
        side='bottom'
        trigger='trigger-hip-adduction'
        triggerAction='click'>
        <IonGrid>
          <IonRow className='ion-align-items-center'>
            <IonCol className='ion-padding-end' size='auto'>
              <IonThumbnail>
                <IonImg src={HipCoronalMovement} />
              </IonThumbnail>
            </IonCol>
            <IonCol>
              <BulletList
                listItems={[
                  'Adduction <10°: Adduction less than 10°',
                  'Fair: Adduction between 10° & 20°',
                  'Poor: Adduction more than 20°',
                  'Adduction >0°: Abduction detected°',
                ]}
              />
            </IonCol>
          </IonRow>
        </IonGrid>
      </Tooltip>
      <Tooltip
        alignment='center'
        dismissOnSelect={true}
        side='bottom'
        trigger='trigger-hip-internal-rotation'
        triggerAction='click'>
        <IonGrid>
          <IonRow className='ion-align-items-center'>
            <IonCol className='ion-padding-end' size='auto'>
              <IonThumbnail>
                <IonImg src={HipTransversalMovement} />
              </IonThumbnail>
            </IonCol>
            <IonCol>
              <BulletList
                listItems={[
                  'Rotation <-10°: Rotation is less than 10°',
                  'Fair: Rotation between -10° & -20°',
                  'Poor: Rotation more than -20°',
                  'Rotation >0°: Hip exorotates°',
                ]}
              />
            </IonCol>
          </IonRow>
        </IonGrid>
      </Tooltip>
      <Tooltip
        alignment='center'
        dismissOnSelect={true}
        reference='trigger'
        side='bottom'
        trigger='trigger-knee-valgus'
        triggerAction='click'>
        <IonGrid>
          <IonRow className='ion-align-items-center'>
            <IonCol className='ion-padding-end' size='auto'>
              <IonThumbnail>
                <IonImg src={KneeCoronalMovement} />
              </IonThumbnail>
            </IonCol>
            <IonCol>
              <BulletList
                listItems={[
                  'Valgus <10°',
                  'Fair: Valgus between 10° and 20°',
                  'Poor: Valgus more than 20°',
                  'Varus: Knee move outward',
                ]}
              />
            </IonCol>
          </IonRow>
        </IonGrid>
      </Tooltip>
    </>
  );
};
